import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import { setFieldModalVisibilty, setOnEditField, editEvaluationField } from "../../../components/evaluations/evaluationsSlice";
import Langs, { Localize } from "../../../app/lang/langs";
import styles from "../Settings.module.css";
import './SettingsModal.css';

// 16px keeps iOS Safari from zooming the whole page when the field takes focus —
// on a full-screen dialog that leaves the user stranded off-canvas with no way back.
const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        '& fieldset': { borderColor: 'rgba(0, 40, 128, 0.12)' },
        '&:hover fieldset': { borderColor: 'rgba(0, 40, 128, 0.25)' },
        '&.Mui-focused fieldset': { borderColor: 'rgba(255, 144, 21, 0.5)', borderWidth: '1.5px' },
    },
    '& .MuiInputBase-input': { fontSize: '16px', minHeight: 28 },
}

/** Label above the control on a phone, beside it from `md` up (the desktop shape). */
function FormRow({ label, children }) {
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '170px 1fr' },
                alignItems: { xs: 'stretch', md: 'start' },
                columnGap: 2,
                rowGap: 1,
                py: 1.5,
                borderBottom: '1px solid rgba(0, 40, 128, 0.07)',
                '&:last-of-type': { borderBottom: 'none' },
            }}
        >
            <Box
                sx={{
                    fontWeight: 600,
                    fontSize: { xs: '0.95rem', md: '0.9rem' },
                    color: 'rgb(0, 40, 128)',
                    letterSpacing: '-0.01em',
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: { md: 40 },
                }}
            >
                {label}
            </Box>
            <Box sx={{ minWidth: 0 }}>{children}</Box>
        </Box>
    )
}

export default function EvaluationFieldModal() {

    const dispatch = useDispatch();

    const visible = useSelector((state) => state.evaluations.fieldModal);
    const onEditField = useSelector((state) => state.evaluations.onEditField)
    const fields = useSelector((state) => state.evaluations.fieldsIgnoreActive)
    const isMobile = useSelector((state) => state.settings.isMobile)
    const isPhone = isMobile === 2

    const [title, setTitle] = useState('')
    const [active, setActive] = useState(false)
    const [teamImpact, setTeamImpact] = useState(false)
    const [currentField, setCurrentField] = useState(null)
    const [titleClass, setTitleClass] = useState(styles.rowShadeGreen)

    const titleInvalid = titleClass === styles.rowShadeRed

    // Depends on `fields` too: if the list arrives after onEditField is set, an effect
    // keyed only on onEditField never re-runs and the form keeps its defaults — which
    // for a toggle means silently showing "off" for a value that is on.
    useEffect(() => {
        const field = fields.find(item => item.id == onEditField);
        if (field) {
            setCurrentField(field)
            setTitle(field.title)
            setActive(field.active)
            setTeamImpact(Number(field.team_impact) === 1)
        }
    }, [onEditField, fields])

    const handleClose = () => {
        dispatch(setFieldModalVisibilty(false));
        dispatch(setOnEditField(null));
        setCurrentField(null);
        setTitle('');
        setActive(null);
        setTeamImpact(false);
        setTitleClass(styles.rowShadeGreen);
    }
    const handleSave = () => {
        if (title.trim() != '' && title != null) {
            if (currentField) {
                const body = {
                    id: currentField.id,
                    title: title,
                    active: active,
                    team_impact: teamImpact
                }
                dispatch(editEvaluationField(body))
                handleClose()
            }
        } else {
            setTitleClass(styles.rowShadeRed)
        }
    }

    //html
    return (
        <Dialog
            className="evalSettingsMuiDialog"
            open={!!visible}
            onClose={handleClose}
            fullScreen={isPhone}
            fullWidth
            maxWidth="sm"
            aria-labelledby="evaluation-field-modal-title"
            PaperProps={{
                sx: {
                    borderRadius: isPhone ? 0 : '16px',
                    border: '1px solid rgba(0, 40, 128, 0.06)',
                },
            }}
        >
            <DialogTitle
                id="evaluation-field-modal-title"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    background: 'linear-gradient(135deg, #fff5eb, #f0f2f8)',
                    borderBottom: '1px solid rgba(0, 40, 128, 0.1)',
                    px: 2.5,
                    py: 2,
                    pt: isPhone ? 'calc(env(safe-area-inset-top, 0px) + 16px)' : 2,
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    color: 'rgb(0, 40, 128)',
                    letterSpacing: '-0.02em',
                }}
            >
                <Langs str="EDIT_QUESTION" />
                <IconButton
                    onClick={handleClose}
                    aria-label={Localize('CLOSE')}
                    sx={{ width: 44, height: 44, color: 'rgba(0, 40, 128, 0.6)' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ px: { xs: 2, md: 3 }, py: 1 }}>
                <FormRow label={<Langs str="TITLE" />}>
                    <TextField
                        value={title}
                        onChange={(e) => { setTitle(e.target.value) }}
                        size="small"
                        fullWidth
                        autoComplete="off"
                        error={titleInvalid}
                        // Red alone is not a signal a colour-blind user can read, so the
                        // reason is spelled out under the field as well.
                        helperText={titleInvalid ? <Langs str="EVAL_TITLE_REQUIRED" /> : ' '}
                        InputProps={{ className: titleClass }}
                        sx={fieldSx}
                    />
                </FormRow>

                <FormRow label={<Langs str="VISIBLTY" />}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={!!active}
                                onChange={(e) => setActive(e.target.checked)}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#FF9015' },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FF9015' },
                                }}
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: '0.95rem', color: 'rgba(0, 40, 128, 0.75)' }}>
                                <Langs str={active ? 'active' : 'inactive'} />
                            </Typography>
                        }
                        sx={{ ml: 0, mr: 0, minHeight: 48 }}
                    />
                </FormRow>

                {/* A 'no' on a team-impact field zeroes the prime for every member of
                    that team for the quarter, not just this person's. Only applied
                    when the type's own team-impact switch is on. */}
                <FormRow label={<Langs str="EVAL_FIELD_TEAM_IMPACT" />}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={teamImpact}
                                onChange={(e) => setTeamImpact(e.target.checked)}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#FF9015' },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FF9015' },
                                }}
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: '0.95rem', color: 'rgba(0, 40, 128, 0.75)' }}>
                                <Langs str={teamImpact ? 'YES' : 'NO'} />
                            </Typography>
                        }
                        sx={{ ml: 0, mr: 0, minHeight: 48 }}
                    />
                    <Typography
                        variant="caption"
                        sx={{ display: 'block', color: 'rgba(45, 42, 38, 0.65)', lineHeight: 1.45 }}
                    >
                        <Langs str="EVAL_FIELD_TEAM_IMPACT_HINT" />
                    </Typography>
                </FormRow>
            </DialogContent>

            <DialogActions
                sx={{
                    px: 2.5,
                    py: 2,
                    pb: isPhone ? 'calc(env(safe-area-inset-bottom, 0px) + 16px)' : 2,
                    gap: 2,
                    background: '#f8f9fc',
                    borderTop: '1px solid rgba(0, 40, 128, 0.08)',
                }}
            >
                <Button
                    onClick={handleClose}
                    sx={{
                        minHeight: 48,
                        flex: { xs: 1, md: '0 0 auto' },
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 600,
                        color: 'rgba(0, 40, 128, 0.7)',
                    }}
                >
                    <Langs str="cancel" />
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    sx={{
                        minHeight: 48,
                        flex: { xs: 1, md: '0 0 auto' },
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: 'none',
                        backgroundColor: '#FF9015',
                        '&:hover': { backgroundColor: '#e57f0d', boxShadow: 'none' },
                    }}
                >
                    <Langs str="SAVE" />
                </Button>
            </DialogActions>
        </Dialog>
    )
}
